const MIN_OLS_POINTS = 3;
const MIN_OLS_POINTS_PER_MOB = 2;

export function hasCohortSheet(cohortSheet) {
  return (cohortSheet?.cohorts?.length ?? 0) > 0;
}

function maxMobFromCohortSheet(cohortSheet) {
  return (
    cohortSheet.cohorts.reduce((maxMob, cohort) => {
      const cohortMax = (cohort.periods || []).reduce(
        (mobMax, period) => Math.max(mobMax, period.mob),
        -1
      );
      return Math.max(maxMob, cohortMax);
    }, -1) + 1
  );
}

/** Flatten Markov-filled square → (spend, 1/ROAS, mob) for OLS. */
export function buildHillOlsRows(cohortSheet) {
  if (!hasCohortSheet(cohortSheet)) {
    return [];
  }

  const rows = [];
  for (const cohort of cohortSheet.cohorts) {
    const spend = Number(cohort.marketingSpend) || 0;
    if (spend <= 0) {
      continue;
    }
    for (const period of cohort.periods || []) {
      const roas = Number(period.roas);
      if (roas > 0 && Number.isFinite(roas)) {
        rows.push({
          x: spend,
          y: 1 / roas,
          mob: period.mob,
          roas,
        });
      }
    }
  }
  return rows;
}

function fitKFromOlsRows(rows) {
  const ols = fitLinearOls(rows, MIN_OLS_POINTS_PER_MOB);
  if (!ols || ols.b <= 0 || ols.a <= 0) {
    return null;
  }
  const k = ols.a / ols.b;
  if (!Number.isFinite(k) || k <= 0) {
    return null;
  }
  return { k, ols };
}

/** OLS rows for one MOB across all cohorts. */
export function buildHillOlsRowsForMob(cohortSheet, mob) {
  if (!hasCohortSheet(cohortSheet)) {
    return [];
  }

  const rows = [];
  for (const cohort of cohortSheet.cohorts) {
    const spend = Number(cohort.marketingSpend) || 0;
    if (spend <= 0) {
      continue;
    }
    const period = (cohort.periods || []).find((entry) => entry.mob === mob);
    const roas = Number(period?.roas);
    if (roas > 0 && Number.isFinite(roas)) {
      rows.push({
        x: spend,
        y: 1 / roas,
        mob,
        roas,
      });
    }
  }
  return rows;
}

function fitLinearOls(rows, minPoints = MIN_OLS_POINTS) {
  const n = rows.length;
  if (n < minPoints) {
    return null;
  }

  const xMean = rows.reduce((sum, row) => sum + row.x, 0) / n;
  const yMean = rows.reduce((sum, row) => sum + row.y, 0) / n;
  let numerator = 0;
  let denominator = 0;
  for (const row of rows) {
    numerator += (row.x - xMean) * (row.y - yMean);
    denominator += (row.x - xMean) ** 2;
  }
  if (denominator === 0) {
    return null;
  }

  const b = numerator / denominator;
  const a = yMean - b * xMean;
  return { a, b, n, xMean, yMean };
}

/** Fit K per MOB, then average across MOBs with valid fits. */
export function computeAverageK(cohortSheet) {
  if (!hasCohortSheet(cohortSheet)) {
    return null;
  }

  const numMobs = maxMobFromCohortSheet(cohortSheet);
  const kByMob = [];
  let pointCount = 0;

  for (let mob = 0; mob < numMobs; mob += 1) {
    const rows = buildHillOlsRowsForMob(cohortSheet, mob);
    pointCount += rows.length;
    const fit = fitKFromOlsRows(rows);
    if (fit) {
      kByMob.push({
        mob,
        k: fit.k,
        pointCount: rows.length,
      });
    }
  }

  if (!kByMob.length) {
    return null;
  }

  const k = kByMob.reduce((sum, entry) => sum + entry.k, 0) / kByMob.length;
  return {
    k,
    kByMob,
    kMobCount: kByMob.length,
    pointCount,
  };
}

function computeMaxRoasByMob(cohortSheet, k) {
  const numMobs = maxMobFromCohortSheet(cohortSheet);
  const maxRoasByMob = Array(numMobs).fill(0);

  for (let mob = 0; mob < numMobs; mob += 1) {
    const implied = [];
    for (const cohort of cohortSheet.cohorts) {
      const spend = Number(cohort.marketingSpend) || 0;
      const period = (cohort.periods || []).find((entry) => entry.mob === mob);
      const roas = Number(period?.roas);
      if (spend > 0 && roas > 0 && Number.isFinite(roas)) {
        implied.push((roas * (k + spend)) / k);
      }
    }
    if (implied.length) {
      maxRoasByMob[mob] =
        implied.reduce((sum, value) => sum + value, 0) / implied.length;
    }
  }

  return maxRoasByMob;
}

function computeHillRSquared(cohortSheet, k, maxRoasByMob) {
  const actuals = [];
  const predicted = [];

  for (const cohort of cohortSheet.cohorts) {
    const spend = Number(cohort.marketingSpend) || 0;
    if (spend <= 0) {
      continue;
    }
    for (const period of cohort.periods || []) {
      const actual = Number(period.roas);
      const maxRoas = maxRoasByMob[period.mob];
      if (actual > 0 && maxRoas > 0 && Number.isFinite(actual)) {
        actuals.push(actual);
        predicted.push((maxRoas * k) / (k + spend));
      }
    }
  }

  if (actuals.length < 2) {
    return null;
  }

  const meanActual =
    actuals.reduce((sum, value) => sum + value, 0) / actuals.length;
  let ssRes = 0;
  let ssTot = 0;
  for (let i = 0; i < actuals.length; i += 1) {
    ssRes += (actuals[i] - predicted[i]) ** 2;
    ssTot += (actuals[i] - meanActual) ** 2;
  }
  if (ssTot === 0) {
    return 1;
  }
  return 1 - ssRes / ssTot;
}

export const HILL_FIT_NO_SATURATION = 'no_saturation';
export const HILL_FIT_TOO_FEW_POINTS = 'too_few_points';

/** Explain why fitHillRoasModel returned null (UI messaging only). */
export function diagnoseHillFit(cohortSheet) {
  if (!hasCohortSheet(cohortSheet)) {
    return HILL_FIT_TOO_FEW_POINTS;
  }
  const numMobs = maxMobFromCohortSheet(cohortSheet);
  let anyMobHadEnoughPoints = false;
  for (let mob = 0; mob < numMobs; mob += 1) {
    const rows = buildHillOlsRowsForMob(cohortSheet, mob);
    if (rows.length >= MIN_OLS_POINTS_PER_MOB) {
      anyMobHadEnoughPoints = true;
    }
    if (fitKFromOlsRows(rows)) {
      return null;
    }
  }
  return anyMobHadEnoughPoints
    ? HILL_FIT_NO_SATURATION
    : HILL_FIT_TOO_FEW_POINTS;
}

/** OLS Hill fit on Markov-filled cohort square. */
export function fitHillRoasModel(cohortSheet) {
  if (!hasCohortSheet(cohortSheet)) {
    return null;
  }

  const averageK = computeAverageK(cohortSheet);
  if (!averageK) {
    return null;
  }

  const { k, kByMob, kMobCount, pointCount } = averageK;
  const maxRoasByMob = computeMaxRoasByMob(cohortSheet, k);
  if (!maxRoasByMob.some((value) => value > 0)) {
    return null;
  }

  const rSquared = computeHillRSquared(cohortSheet, k, maxRoasByMob);

  return {
    k,
    kByMob,
    kMobCount,
    maxRoasByMob,
    rSquared,
    pointCount,
    numMobs: maxRoasByMob.length,
  };
}

function enforceMonotonicCumulative(decimals) {
  let prev = 0;
  return decimals.map((value) => {
    const next = Math.max(prev, Number(value) || 0);
    prev = next;
    return next;
  });
}

/** Predict cumulative ROAS decimals for a spend amount. */
export function predictRoasVector(model, spend) {
  if (!model?.maxRoasByMob?.length) {
    return [];
  }

  const currentSpend = Math.max(0, Number(spend) || 0);
  const factor =
    currentSpend === 0 ? 1 : model.k / (model.k + currentSpend);

  const decimals = model.maxRoasByMob.map((maxRoas) =>
    maxRoas > 0 ? maxRoas * factor : 0
  );
  return enforceMonotonicCumulative(decimals);
}

/** Predict cumulative ROAS as percent values for runCalc. */
export function predictRoasVectorPct(model, spend) {
  return predictRoasVector(model, spend).map((value) => value * 100);
}

export function buildSpendRoasVectors(cohortSheet, marketingSpends) {
  const model = fitHillRoasModel(cohortSheet);
  if (!model) {
    return { model: null, vectors: null };
  }
  return {
    model,
    vectors: (marketingSpends || []).map((spend) =>
      predictRoasVectorPct(model, spend)
    ),
  };
}

export function formatRoasVectorForCopy(vectorPct) {
  if (!vectorPct?.length) {
    return '';
  }
  return vectorPct.map((value) => Number(value).toFixed(1)).join(', ');
}

export function maxRoasVectorPct(model) {
  return (model?.maxRoasByMob ?? []).map((value) => value * 100);
}

export function formatMaxRoasVectorForCopy(model) {
  return formatRoasVectorForCopy(maxRoasVectorPct(model));
}

export function formatHillModelSummary(model) {
  if (!model?.k) {
    return null;
  }
  const kLabel = `$${Math.round(model.k).toLocaleString()}`;
  const mobCount = model.kMobCount ?? model.kByMob?.length ?? 0;
  const r2Label =
    model.rSquared != null ? ` · R² ${model.rSquared.toFixed(2)}` : '';
  return `Hill model on Markov-filled square · avg k ${kLabel} (mean of ${mobCount} MOB${mobCount === 1 ? '' : 's'})${r2Label} · ${model.pointCount} data points · M0–M${model.numMobs - 1}`;
}

export function marginalFromCumulativeVector(vectorPct) {
  return vectorPct.map((value, index) =>
    index === 0 ? value : value - vectorPct[index - 1]
  );
}
